import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Upload, UserPlus, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminClientEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', display_name: '', role: 'client' });

  useEffect(() => {
    api.get('/admin/tenants/' + id).then(res => {
      setTenant(res.data);
      setUsers(res.data.users || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/tenants/' + id, tenant);
      toast.success('Cliente actualizado');
    } catch (err) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await api.post('/admin/tenants/' + tenant.subdomain + '/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTenant({ ...tenant, logo_url: res.data.logo_url });
      toast.success('Logo actualizado');
    } catch { toast.error('Error al subir logo'); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/tenants/' + id + '/users', newUser);
      toast.success('Usuario creado');
      setShowAddUser(false);
      setNewUser({ username: '', password: '', display_name: '', role: 'client' });
      const res = await api.get('/admin/tenants/' + id);
      setUsers(res.data.users || []);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const toggleUser = async (userId) => {
    try {
      await api.put('/admin/users/' + userId + '/toggle');
      const res = await api.get('/admin/tenants/' + id);
      setUsers(res.data.users || []);
      toast.success('Usuario actualizado');
    } catch { toast.error('Error'); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;
  if (!tenant) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cliente no encontrado</div>;

  return (
    <div>
      <button className="btn btn-ghost" onClick={() => navigate('/clients')} style={{ marginBottom: 16 }}><ArrowLeft size={16} /> Volver</button>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 24 }}>{tenant.name}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Datos generales</h3>
          <div className="form-group"><label>Nombre</label><input value={tenant.name || ''} onChange={e => setTenant({...tenant, name: e.target.value})} /></div>
          <div className="form-group"><label>Empresa</label><input value={tenant.company_name || ''} onChange={e => setTenant({...tenant, company_name: e.target.value})} /></div>
          <div className="form-group"><label>Subdominio</label><input value={tenant.subdomain} disabled style={{ opacity: 0.6 }} /></div>
          <div className="form-group"><label>Código país</label><input value={tenant.default_country_code || ''} onChange={e => setTenant({...tenant, default_country_code: e.target.value})} /></div>
          <div className="form-group">
            <label>Estado</label>
            <button className="btn btn-ghost" onClick={() => setTenant({...tenant, is_active: tenant.is_active ? 0 : 1})}>
              {tenant.is_active ? <><ToggleRight size={18} style={{ color: 'var(--success)' }} /> Activo</> : <><ToggleLeft size={18} style={{ color: 'var(--danger)' }} /> Inactivo</>}
            </button>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Branding</h3>
          <div className="form-group">
            <label>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {tenant.logo_url && <img src={tenant.logo_url} alt="Logo" style={{ height: 40, background: '#1a1f2e', padding: 8, borderRadius: 8 }} />}
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}><Upload size={14} /> Subir logo<input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} /></label>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Color principal</label><div style={{ display: 'flex', gap: 8 }}><input type="color" value={tenant.primary_color || '#2E7DE2'} onChange={e => setTenant({...tenant, primary_color: e.target.value})} style={{ width: 40, height: 36, padding: 2 }} /><input value={tenant.primary_color || ''} onChange={e => setTenant({...tenant, primary_color: e.target.value})} style={{ fontSize: 12 }} /></div></div>
            <div className="form-group"><label>Fondo</label><div style={{ display: 'flex', gap: 8 }}><input type="color" value={tenant.background_color || '#0a0d14'} onChange={e => setTenant({...tenant, background_color: e.target.value})} style={{ width: 40, height: 36, padding: 2 }} /><input value={tenant.background_color || ''} onChange={e => setTenant({...tenant, background_color: e.target.value})} style={{ fontSize: 12 }} /></div></div>
          </div>
          <div className="form-group"><label>Secundario</label><div style={{ display: 'flex', gap: 8 }}><input type="color" value={tenant.secondary_color || '#161b28'} onChange={e => setTenant({...tenant, secondary_color: e.target.value})} style={{ width: 40, height: 36, padding: 2 }} /><input value={tenant.secondary_color || ''} onChange={e => setTenant({...tenant, secondary_color: e.target.value})} style={{ fontSize: 12 }} /></div></div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Integraciones</h3>
          <div className="form-group"><label>Token Apify / Research</label><input value={tenant.apify_token || ''} onChange={e => setTenant({...tenant, apify_token: e.target.value})} placeholder="apify_api_..." /></div>
          <div className="form-group"><label>Webhook Key (Google Ads)</label><input value={tenant.google_ads_webhook_key || ''} onChange={e => setTenant({...tenant, google_ads_webhook_key: e.target.value})} /></div>
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>M</span> Meta Ads
            </div>
            <div className="form-group"><label>Token de Meta Ads (System User)</label><input value={tenant.meta_ads_token || ''} onChange={e => setTenant({...tenant, meta_ads_token: e.target.value})} placeholder="EAAxxxxxxx..." style={{ fontSize: 12 }} /></div>
            <div className="form-group"><label>ID de Cuenta Publicitaria</label><input value={tenant.meta_ads_account_id || ''} onChange={e => setTenant({...tenant, meta_ads_account_id: e.target.value})} placeholder="Ej: 708428203237366" /></div>
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Usuarios</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAddUser(true)}><UserPlus size={14} /> Agregar</button>
          </div>
          {users.map(u => (
            <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.display_name || u.username}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.username} — {u.role}</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => toggleUser(u.id)}>
                {u.is_active ? <ToggleRight size={16} style={{ color: 'var(--success)' }} /> : <ToggleLeft size={16} style={{ color: 'var(--danger)' }} />}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar cambios'}</button>
      </div>

      {showAddUser && (
        <div className="modal-overlay" onClick={() => setShowAddUser(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2>Nuevo usuario</h2>
            <form onSubmit={handleAddUser}>
              <div className="form-group"><label>Usuario</label><input value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required /></div>
              <div className="form-group"><label>Contraseña</label><input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required /></div>
              <div className="form-group"><label>Nombre</label><input value={newUser.display_name} onChange={e => setNewUser({...newUser, display_name: e.target.value})} /></div>
              <div className="form-group"><label>Rol</label>
                <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                  <option value="admin">Admin</option>
                  <option value="client">Cliente</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddUser(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
